import { memoize } from "lodash";
import getCoreUIStore from "../../core/core-ui-store";
import { computed, observable, runInAction } from "mobx";
import { TFlaggedDescriptionsArray, TBannedUsersArray } from "../../../Types";
import getCorePopUpStore from "../../core/core-popup-store";
import { isDefinedString, isPositiveNumber } from "et3-type-guards";
import {
  StringOrNumber,
  TEt3PickerSingleValue,
  TEt3PickerOption
} from "../../../TypesNEW";
import { apiAddFlaggedDescriptions } from "../../../api/supervisor-api/api-call-add-flagged-description";
import { apiDeletedFlaggedDescriptions } from "../../../api/supervisor-api/api-call-deleted-flagged-description";
import { apiGetBannedUsers } from "../../../api/supervisor-api/api-call-get-banned-users";
import { apiGetFlaggedDescription } from "../../../api/supervisor-api/api-call-get-flagged-description";
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_PERCENTAGE_VALUE,
  MIN_PERCENTAGE_VALUE,
  MIN_DESCRIPTION_LENGTH
} from "../../../constants/input-entries-constants";

class FlaggedDesctiprion {
  flaggedDescriptionsText = observable.box<string>("");
  flaggedDescriptionsPercentage = observable.box<number>(MIN_PERCENTAGE_VALUE);
  flaggedDescriptions = observable.box<TFlaggedDescriptionsArray>([]);
  userBannedDescriptions = observable.box<TEt3PickerOption[]>([]);
  bannedUsersDescription = observable.box<TEt3PickerSingleValue>({
    label: "",
    value: ""
  });
  bannedUsers = observable.box<TBannedUsersArray>([]);

  allowAddingFlaggedDescription = computed(() => {
    const text = this.flaggedDescriptionsText.get().trim();
    const percentage = this.flaggedDescriptionsPercentage.get();
    const descriptionValidText =
      isDefinedString(text) &&
      text.length >= MIN_DESCRIPTION_LENGTH &&
      text.length <= MAX_DESCRIPTION_LENGTH;
    const descriptionValidPercentage =
      isPositiveNumber(percentage) &&
      percentage >= MIN_PERCENTAGE_VALUE &&
      percentage <= MAX_PERCENTAGE_VALUE;
    return descriptionValidText && descriptionValidPercentage;
  });

  getFlaggedDescriptions = async () => {
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", true);
    const { status, data, code } = await apiGetFlaggedDescription();
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", false);
    this.setToDefault();
    if (status === "OK") {
      this.flaggedDescriptions.set(data);
      return;
    }
    getCorePopUpStore().showAlertMessage(code);
  };

  addFlaggedDescriptions = async () => {
    const text = this.flaggedDescriptionsText.get();
    const percentage = this.flaggedDescriptionsPercentage.get();
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", true);
    const { status, code } = await apiAddFlaggedDescriptions(text, percentage);
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", false);
    this.setToDefault();
    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage("DESCRIPTION_ADDED");
      this.userBannedDescriptions.get().unshift({
        value: text,
        label: text
      });
      this.getFlaggedDescriptions();
      return;
    }
    getCorePopUpStore().showAlertMessage(code);
  };

  deleteFlaggedDescriptions = async (deletedID: number) => {
    if (!isPositiveNumber(deletedID)) {
      getCorePopUpStore().showAlertMessage("INVALID_INPUT");
      return;
    }
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", true);
    const { status, code } = await apiDeletedFlaggedDescriptions(deletedID);
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", false);
    this.setToDefault();
    if (status === "OK") {
      const filteredDescription = this.flaggedDescriptions
        .get()
        .filter((description) => {
          return deletedID !== description.deletedID;
        });
      const finalDescritpion = filteredDescription.map((description) => {
        return {
          value: description.description,
          label: description.description
        };
      });
      this.userBannedDescriptions.set(finalDescritpion);
      this.flaggedDescriptions.set(filteredDescription);
      getCorePopUpStore().showSuccessMessage("DESCRIPTION_DELETED");
      this.getFlaggedDescriptions();
      return;
    }
    getCorePopUpStore().showAlertMessage(code);
  };

  getBannedUsers = async () => {
    const bannedUsersDescriptionValue = this.bannedUsersDescription.get().value;
    const bannedUsersDescription = isDefinedString(bannedUsersDescriptionValue)
      ? `${bannedUsersDescriptionValue}`
      : " ";
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", true);
    const { status, data, code } = await apiGetBannedUsers(
      bannedUsersDescription
    );
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", false);
    this.setToDefault();
    if (status === "OK") {
      this.bannedUsers.set(data);
      return;
    }
    getCorePopUpStore().showAlertMessage(code);
  };

  getBannedUsersDescription = async () => {
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", true);
    const { status, data, code } = await apiGetFlaggedDescription();
    getCoreUIStore().setIsLoading("SUPERVISOR_FLAGGED_DESCRIPTIONS", false);
    if (status === "OK") {
      const userBannedDescriptions = data.map((descriptionData) => {
        return {
          label: descriptionData.description,
          value: descriptionData.description
        };
      });
      this.userBannedDescriptions.set(userBannedDescriptions);
      return;
    }
    getCorePopUpStore().showAlertMessage(code);
  };

  setToDefault = () => {
    runInAction(() => {
      this.flaggedDescriptionsText.set("");
      this.flaggedDescriptionsPercentage.set(MIN_PERCENTAGE_VALUE);
    });
  };

  setflaggedDescriptionsText = async (value: StringOrNumber) => {
    const stringifiedValue = `${value}`;
    runInAction(() => {
      if (stringifiedValue.length < MAX_DESCRIPTION_LENGTH) {
        this.flaggedDescriptionsText.set(stringifiedValue);
      }
      return;
    });
  };

  setflaggedDescriptionsPercentage = async (value: StringOrNumber) => {
    const stringifiedValue = `${value}`;
    const parsedValue = parseInt(stringifiedValue, 10);
    runInAction(() => {
      if (parsedValue <= MAX_PERCENTAGE_VALUE) {
        this.flaggedDescriptionsPercentage.set(parsedValue);
      }
      return;
    });
  };

  setBannedUsersDescription = (value: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.bannedUsersDescription.set(value);
    });
  };
}

const getFlaggedDesctiprionStore = memoize(
  () => new FlaggedDesctiprion(),
  () => 1
);

export default getFlaggedDesctiprionStore;
